import type { AnimationDefinition } from 'motion/react'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

/**
 * Fallback delay, in milliseconds, after which an overlay stuck in the
 * `exiting` state is forced to `closed`. Must comfortably exceed the overlay
 * exit animations (~300ms) so it never interrupts a normal close.
 */
const EXIT_ANIMATION_FALLBACK_MS = 700

/**
 * Context providing animation state props for modal animations.
 */
export const AnimationStateContext =
  createContext<ModalAnimationStateProps | null>(null)

/**
 * Props for the animation state provider component.
 */
interface AnimationStatePropsProviderProps {
  /** Animation state props to be provided through context */
  props: ModalAnimationStateProps
  /** Child components that can consume the animation context */
  children: ReactNode
}

/**
 * Provider component for animation state props.
 * Makes animation state available to child components through context.
 */
export function AnimationStatePropsProvider({
  props,
  children,
}: AnimationStatePropsProviderProps) {
  return (
    <AnimationStateContext.Provider value={props}>
      {children}
    </AnimationStateContext.Provider>
  )
}

/**
 * Hook to access modal animation state props from context.
 *
 * @returns The current modal animation state props
 */
export function useModalAnimationStateProps() {
  return useContext(AnimationStateContext)
}

/**
 * Props for controlling modal animation states.
 */
interface ModalAnimationStateControlsProps {
  /**
   * Whether the modal is currently open.
   */
  isOpen: boolean
  /**
   * The animation variant name to use when visible.
   */
  visibleVariant: string
  /**
   * The animation variant name to use when hidden.
   */
  hiddenVariant: string
}

/**
 * Animation state props used by modal animations.
 */
export interface ModalAnimationStateProps {
  /**
   * Initial animation state.
   *
   * This is the state that will be used when the modal is first mounted.
   */
  initial: string
  /**
   * Current animation state to animate to.
   */
  animate: string
  /**
   * Animation state when exiting.
   *
   * This is the state that will be used when the modal is closing.
   */
  exit: string
  /**
   * Callback when animation completes.
   * Will be supplied to the main animating component, like the overlay.
   */
  onAnimationComplete: (animation: AnimationDefinition) => void
  /**
   * Whether the modal is currently exiting.
   */
  isExiting: boolean
}

/**
 * Hook that manages animation state controls for modals.
 *
 * Handles transitions between visible and hidden states based on the isOpen prop,
 * tracks animation completion, and manages mounting/unmounting states.
 *
 * This is necessary due to how React Aria works.
 *
 * @see https://react-spectrum.adobe.com/react-aria/styling.html#framer-motion
 *
 * @param props Configuration props for modal animation
 * @returns Animation state props to be passed to animation components
 */
export function useModalAnimationStateControls({
  isOpen,
  visibleVariant,
  hiddenVariant,
}: ModalAnimationStateControlsProps): ModalAnimationStateProps {
  const [exitState, setExitState] = useState<'closed' | 'open' | 'exiting'>(
    isOpen ? 'open' : 'closed',
  )

  const onAnimationComplete = useCallback(
    (animation: AnimationDefinition) => {
      if (animation !== hiddenVariant) {
        return
      }
      if (exitState === 'exiting' && !isOpen) {
        setExitState('closed')
      }
    },
    [exitState, hiddenVariant, isOpen],
  )

  useLayoutEffect(() => {
    switch (exitState) {
      case 'open': {
        // If isOpen becomes false, set the state to exiting.
        if (!isOpen) {
          setExitState('exiting')
        }
        break
      }
      case 'closed':
      case 'exiting': {
        // If we are exiting and isOpen becomes true, the animation was interrupted.
        // Reset the state to open.
        if (isOpen) {
          setExitState('open')
        }
        break
      }
    }
  }, [exitState, isOpen])

  // react-spectrum #9158: on rapid open/close the Framer Motion
  // `onAnimationComplete` callback can be dropped (the motion effect runs before
  // the element ref is attached, so the completion listener never registers),
  // leaving the overlay stuck in `exiting` — mounted at opacity 0, where it
  // intercepts clicks and E2E queries. Guarantee the transition to `closed` with
  // a fallback timer. Normal closes fire `onAnimationComplete` first and clear it.
  useEffect(() => {
    if (exitState !== 'exiting' || isOpen) {
      return
    }
    const timer = setTimeout(
      () => setExitState('closed'),
      EXIT_ANIMATION_FALLBACK_MS,
    )
    return () => clearTimeout(timer)
  }, [exitState, isOpen])

  return useMemo((): ModalAnimationStateProps => {
    return {
      initial: hiddenVariant,
      animate:
        exitState === 'open'
          ? visibleVariant
          : exitState === 'exiting'
            ? hiddenVariant
            : '_unmounted_',
      exit: hiddenVariant,
      onAnimationComplete: onAnimationComplete,
      isExiting: exitState === 'exiting',
    }
  }, [exitState, visibleVariant, hiddenVariant, onAnimationComplete])
}
